import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import api from '@/lib/api'
import { AUTH_CHANGED_EVENT } from '@/lib/serverContext'
import { SESSION_EXPIRED_EVENT } from '@/lib/authExpiry'
import { clearCurrentTenantSensitiveStorage, tenantStorageKey } from '@/lib/tenantStorage'
import { readOfflineAuthContext, writeOfflineAuthContext } from '@/lib/offlineCache'
import AccountTransitionOverlay from '@/components/AccountTransitionOverlay'
import { clearAuthToken, getAuthToken, replaceAuthToken, setAuthToken } from '@/lib/tokenStorage'

type OrganizationSwitchState = {
  targetLabel: string
  phase: 'authorizing' | 'loading'
} | null

interface AuthContextType {
  token: string | null
  user: string | null
  role: string | null
  capabilities: string[]
  /**
   * false while a token exists but /auth/context has not answered yet (page
   * load, refresh, deep link). capabilities/isPlatformAdmin are only
   * trustworthy once this is true - they start empty, not "denied". Capability
   * gates must wait on this instead of treating an empty list as a verdict.
   */
  authReady: boolean
  organization: { id: string; name: string; slug: string; kind: 'personal' | 'team'; plan: string } | null
  organizations: Array<{ id: string; name: string; slug: string; kind: 'personal' | 'team'; plan: string }>
  isPlatformAdmin: boolean
  organizationSwitch: OrganizationSwitchState
  switchOrganization: (organizationId: string) => Promise<void>
  hasCapability: (capability: string) => boolean
  login: (username: string, password: string, rememberMe?: boolean) => Promise<{ requires_2fa?: boolean; pending_token?: string; username?: string } | void>
  loginPasskey: (username: string, rememberMe?: boolean) => Promise<void>
  loginSsoCode: (code: string, rememberMe?: boolean) => Promise<void>
  login2fa: (pendingToken: string, code: string, trustDevice?: boolean, rememberMe?: boolean) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType>(null!)

export function AuthProvider({ children }: { children: ReactNode }) {
  const storedToken = getAuthToken()
  const initialOfflineContext = storedToken ? readOfflineAuthContext() : null
  const [token, setToken] = useState<string | null>(storedToken)
  const [user, setUser] = useState<string | null>(localStorage.getItem('user'))
  const [role, setRole] = useState<string | null>(localStorage.getItem('role'))
  const [capabilities, setCapabilities] = useState<string[]>(initialOfflineContext?.capabilities || [])
  const [organization, setOrganization] = useState<{ id: string; name: string; slug: string; kind: 'personal' | 'team'; plan: string } | null>(initialOfflineContext?.organization || null)
  const [organizations, setOrganizations] = useState<Array<{ id: string; name: string; slug: string; kind: 'personal' | 'team'; plan: string }>>(initialOfflineContext?.organizations || [])
  const [isPlatformAdmin,setIsPlatformAdmin]=useState(initialOfflineContext?.is_platform_admin || false)
  const [authReady, setAuthReady] = useState(() => !storedToken || initialOfflineContext !== null)
  const [organizationSwitch, setOrganizationSwitch] = useState<OrganizationSwitchState>(null)

  function announceAuthChange() {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))
  }

  function applyContext(context: any, persistOffline = true) {
    const roles: string[] = context?.roles || []
    const nextRole = roles.includes('admin') ? 'admin' : (roles[0] || 'user')
    setRole(nextRole); localStorage.setItem('role', nextRole)
    setCapabilities(context?.capabilities || [])
    setOrganization(context?.organization || null)
    setOrganizations(context?.organizations || [])
    setIsPlatformAdmin(!!context?.is_platform_admin)
    if (persistOffline) writeOfflineAuthContext(context)
  }

  async function switchOrganization(organizationId: string) {
    if (!organizationId || organizationId === organization?.id || organizationSwitch) return
    const target = organizations.find(item => item.id === organizationId)
    setOrganizationSwitch({ targetLabel: target?.name || 'Seçilen çalışma alanı', phase: 'authorizing' })
    try {
      const { data } = await api.post('/auth/context/switch', { organization_id: organizationId })
      setOrganizationSwitch({ targetLabel: data.context?.organization?.name || target?.name || 'Seçilen çalışma alanı', phase: 'loading' })
      replaceAuthToken(data.access_token)
      setToken(data.access_token)
      applyContext(data.context || {})
      announceAuthChange()
      // State'in son aşamasının ekrana çizilmesine izin ver; ardından bütün
      // tenant-kapsamlı provider/cache'ler yeni token ile temiz başlasın.
      window.setTimeout(() => window.location.reload(), 120)
    } catch (error: any) {
      setOrganizationSwitch(null)
      window.dispatchEvent(new CustomEvent('servernox-in-app-notification', {
        detail: {
          event: 'operation.failed',
          title: 'Çalışma alanı değiştirilemedi',
          body: error?.response?.data?.detail || error?.message || 'Yeni çalışma alanı oturumu hazırlanamadı; mevcut hesap korunuyor.',
          url: '/settings',
        },
      }))
    }
  }

  async function registerNativePushIfPossible() {
    try {
      const nativeApi = (window as any).SCPNative
      if (!nativeApi || !nativeApi.isCapacitor || !nativeApi.registerFcmPush) return
      await nativeApi.registerFcmPush()
    } catch {
      // keep auth flow resilient even if push registration fails
    }
  }

  async function rebindBrowserPushIfPresent() {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (!subscription) return
      const value = subscription.toJSON()
      if (!value.keys?.p256dh || !value.keys?.auth) return
      await api.post('/push/subscribe', {
        endpoint: subscription.endpoint,
        keys: { p256dh: value.keys.p256dh, auth: value.keys.auth },
      })
    } catch {
      // Bildirim yeniden bağlama hatası oturum açmayı engellememeli.
    }
  }

  async function detachCurrentDevicePush() {
    const tasks: Promise<unknown>[] = []
    try {
      const nativeApi = (window as any).SCPNative
      if (nativeApi?.unregisterCurrentFcmEndpoint) {
        tasks.push(Promise.resolve(nativeApi.unregisterCurrentFcmEndpoint()))
      }
    } catch {}
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        tasks.push(navigator.serviceWorker.ready.then(async registration => {
          const subscription = await registration.pushManager.getSubscription()
          if (!subscription) return
          await api.delete('/push/subscribe', { data: { endpoint: subscription.endpoint } })
        }))
      }
    } catch {}
    await Promise.allSettled(tasks)
  }

  async function login(username: string, password: string, rememberMe: boolean = false) {
    const params = new URLSearchParams()
    params.append('username', username.trim())
    params.append('password', password.trim())
    const headers: Record<string, string> = { 'Content-Type': 'application/x-www-form-urlencoded' }
    if (rememberMe) headers['X-Remember-Me'] = '1'
    const { data } = await api.post('/auth/login', params, { headers })
    if (data.requires_2fa) return { requires_2fa: true, pending_token: data.pending_token, username }
    setAuthToken(data.access_token, rememberMe)
    localStorage.setItem('user', data.username || username)
    localStorage.setItem('role', data.role || 'user')
    setToken(data.access_token)
    setUser(data.username || username)
    setRole(data.role || 'user')
    // Taze giriş: capabilities/isPlatformAdmin henüz bilinmiyor. Aşağıdaki
    // token değişimi useEffect'i tetikleyip /auth/context'i çekecek ve
    // authReady'i true'ya çevirecek - o ana kadar kapasiteye bağlı rotalar
    // "erişim yok" yerine "yükleniyor" göstermeli.
    setAuthReady(false)
    announceAuthChange()
    // Authentication must finish independently from optional notification
    // transports. Native Firebase initialization and browser push rebinding
    // may be slow or unavailable on a particular device/build.
    void Promise.allSettled([registerNativePushIfPossible(), rebindBrowserPushIfPresent()])
  }

  async function loginPasskey(username: string, rememberMe: boolean = false) {
    const normalizedUsername = username.trim()
    if (!normalizedUsername) throw new Error('Passkey girişi için kullanıcı adınızı yazın.')
    const { data: begin } = await api.post('/auth/passkeys/login/options', { username: normalizedUsername })
    const { startAuthentication } = await import('@simplewebauthn/browser')
    const credential = await startAuthentication({ optionsJSON: begin.options })
    const { data } = await api.post('/auth/passkeys/login/verify', {
      challenge_id: begin.challenge_id,
      username: normalizedUsername,
      remember_me: rememberMe,
      credential,
    })
    setAuthToken(data.access_token, rememberMe)
    localStorage.setItem('user', data.username || normalizedUsername)
    localStorage.setItem('role', data.role || 'user')
    setToken(data.access_token)
    setUser(data.username || normalizedUsername)
    setRole(data.role || 'user')
    setAuthReady(false)
    announceAuthChange()
    void Promise.allSettled([registerNativePushIfPossible(), rebindBrowserPushIfPresent()])
  }

  async function loginSsoCode(code: string, rememberMe: boolean = false) {
    const { data } = await api.post('/auth/sso/exchange', { code, remember_me: rememberMe })
    setAuthToken(data.access_token, rememberMe)
    localStorage.setItem('user', data.username)
    localStorage.setItem('role', data.role || 'user')
    setToken(data.access_token); setUser(data.username); setRole(data.role || 'user'); setAuthReady(false)
    announceAuthChange()
    void Promise.allSettled([registerNativePushIfPossible(), rebindBrowserPushIfPresent()])
  }

  async function login2fa(pendingToken: string, code: string, trustDevice: boolean = true, rememberMe: boolean = false) {
    const headers: Record<string, string> = {}
    if (rememberMe) headers['X-Remember-Me'] = '1'
    const { data } = await api.post('/auth/login/2fa', {
      pending_token: pendingToken,
      code,
      trust_device: trustDevice,
    }, { headers })
    setAuthToken(data.access_token, rememberMe)
    // Backend'den gelen username'i kullan, yoksa localStorage'dakini tut
    if (data.username) localStorage.setItem('user', data.username)
    localStorage.setItem('role', data.role || 'user')
    setToken(data.access_token)
    setUser(localStorage.getItem('user'))
    setRole(data.role || localStorage.getItem('role'))
    setAuthReady(false)
    announceAuthChange()
    void Promise.allSettled([registerNativePushIfPossible(), rebindBrowserPushIfPresent()])
  }

  async function logout() {
    // Token hâlâ geçerliyken bu fiziksel cihazı mevcut üyelikten
    // ayır; aksi halde çıkış ekranında eski hesaba ait push görünebilir.
    await detachCurrentDevicePush()
    try {
      await api.post('/auth/logout')
    } catch {
      // ignore — clear local state regardless
    }
    try {
      clearCurrentTenantSensitiveStorage()
      sessionStorage.removeItem(tenantStorageKey('scp_pending_notification'))
    } catch {}
    clearAuthToken()
    localStorage.removeItem('user')
    localStorage.removeItem('role')
    setToken(null)
    setUser(null)
    setRole(null)
    setCapabilities([])
    setOrganization(null)
    setOrganizations([])
    setIsPlatformAdmin(false)
    setAuthReady(true)
    announceAuthChange()
  }

  useEffect(() => {
    if (!token) return

    let alive = true
    let checking = false
    let lastCheckedAt = 0
    const validateSession = (force = false) => {
      if (!alive || checking || (!force && Date.now() - lastCheckedAt < 30_000)) return
      checking = true
      lastCheckedAt = Date.now()
      api.get('/auth/context').then(response => {
        if (!alive) return
        const context = response.data || {}
        applyContext(context)
        setAuthReady(true)
      }).catch((err) => {
        if (err?.response?.status === 401) {
          try {
            clearCurrentTenantSensitiveStorage()
            sessionStorage.removeItem(tenantStorageKey('scp_pending_notification'))
          } catch {}
          clearAuthToken()
          localStorage.removeItem('user')
          setToken(null)
          setUser(null)
          setRole(null)
          setCapabilities([])
          setOrganization(null)
          setOrganizations([])
          setIsPlatformAdmin(false)
          setAuthReady(true)
          announceAuthChange()
        } else {
          // Ağ hatası / 5xx: son doğrulanmış çalışma alanı ve menü bağlamını
          // salt okunur çevrimdışı kabukta koru. Backend yetki sınırı değişmez
          // ve bağlantı gelir gelmez canlı context bunun yerini alır.
          const cached = readOfflineAuthContext()
          if (cached) applyContext(cached, false)
          setAuthReady(true)
        }
      }).finally(() => { checking = false })
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') validateSession(true)
    }
    const onExpired = () => {
      if (!alive) return
      setToken(null)
      setUser(null)
      setRole(null)
      setCapabilities([])
      setOrganization(null)
      setOrganizations([])
      setIsPlatformAdmin(false)
      setAuthReady(true)
    }

    validateSession(true)
    registerNativePushIfPossible()
    rebindBrowserPushIfPresent()
    window.addEventListener('focus', onVisible)
    window.addEventListener('pageshow', onVisible)
    window.addEventListener('online', onVisible)
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    document.addEventListener('visibilitychange', onVisible)
    const timer = window.setInterval(() => validateSession(), 60_000)

    return () => {
      alive = false
      window.removeEventListener('focus', onVisible)
      window.removeEventListener('pageshow', onVisible)
      window.removeEventListener('online', onVisible)
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
    }
  }, [token])

  return (
    <AuthContext.Provider value={{ token, user, role, capabilities, authReady, organization, organizations, isPlatformAdmin, organizationSwitch, switchOrganization, hasCapability: (capability: string) => capabilities.includes(capability), login, loginPasskey, loginSsoCode, login2fa, logout }}>
      {children}
      <AccountTransitionOverlay
        open={!!organizationSwitch}
        title="Çalışma alanı değiştiriliyor"
        targetLabel={organizationSwitch?.targetLabel}
        detail="Yeni çalışma alanının oturumu, yetkileri, sunucu envanteri ve cihaz bildirim bağlamı hazırlanıyor."
        completedStep={organizationSwitch?.phase === 'loading' ? 'Oturum ve yetkiler doğrulandı' : undefined}
        activeStep={organizationSwitch?.phase === 'loading' ? 'Sunucular ve çalışma alanı açılıyor…' : 'Yeni oturum güvenli biçimde doğrulanıyor…'}
      />
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
